import Box from '@mui/material/Box';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { AppContext } from '../../contexts/app-context';
import { AuthContext } from '../../contexts/auth-context';
import {
  useReportRouteLoading,
  useRetryRoute,
} from '../../hooks/use-route-loading';
import { AllianceFooter } from '../alliance/alliance-footer';
import { LoadFailedScreen } from '../alliance/system-screens';
import { AnimateInProps } from '../animate-in';
import { RouteErrorBoundary } from './route-error-boundary';
import { RouteWrapper } from './route-wrapper';

/**
 * Wraps every top-level page: the document title, then the content (in an
 * error boundary) with the Alliance footer (unless `hideFooter`), full
 * width after the page's bottom padding.
 *
 * While `loading`, the content area is empty and the shell shows the 3px
 * bar under the App Bar after 150ms. `hasError` shows "Couldn't load this
 * page" (offline vs. server trouble) with Try again, which refetches failed
 * queries and remounts the page.
 */
export function RouteContainer(props: {
  children: React.ReactNode;
  routeTitle: string;
  loading?: boolean;
  headerContent?: React.ReactNode;
  hasError?: boolean;
  paddingBottom?: number;
  hideHeader?: boolean;
  hideFooter?: boolean;
  nofade?: AnimateInProps['nofade'];
  notransform?: AnimateInProps['notransform'];
  noanimation?: boolean;
}) {
  // Context
  const { state, dispatch } = React.useContext(AppContext);
  const authContext = React.useContext(AuthContext);
  // Hooks
  const retryRoute = useRetryRoute();
  useReportRouteLoading({ loading: !!props.loading });

  React.useEffect(() => {
    // If the drawer is open when this component mounts, close it
    dispatch({ type: 'DRAWER_CLOSE' });
  }, [dispatch]);

  // The Arrival bar (signed in, an event ready for check-in) is fixed to the
  // bottom of the screen; at md+ the footer's ink band runs on under it so it
  // never covers the legal row. Below md the footer already leaves room.
  const arrivalShowing =
    authContext.state.user !== undefined && state.arrival.events.length > 0;
  const footer = props.hideFooter ? undefined : (
    <Box
      sx={{
        // The footer's ink (FOOTER_COLORS.background in alliance-footer.tsx)
        backgroundColor: '#0e0f10',
        pb: arrivalShowing ? { xs: 0, md: '72px' } : 0,
      }}
    >
      <AllianceFooter />
    </Box>
  );

  let routeContent: JSX.Element | null = null;
  if (props.loading) {
    // The shell shows the 3px bar under the App Bar
    routeContent = null;
  } else if (props.hasError) {
    routeContent = <LoadFailedScreen layout="inline" onRetry={retryRoute} />;
  } else {
    routeContent = (
      <RouteErrorBoundary>
        <RouteWrapper
          paddingBottom={props.paddingBottom}
          nofade={props.nofade}
          notransform={props.notransform}
          noanimation={props.noanimation}
          footer={footer}
        >
          {props.children}
        </RouteWrapper>
      </RouteErrorBoundary>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Alliance Events · {props.routeTitle}</title>
      </Helmet>

      <div role="main">{routeContent}</div>
    </React.Fragment>
  );
}
